import { TablescapeEvent } from "@root/tablescape/utilities/tablescapeContext";
import { type Context } from "@tablescape/state";
import {
  currencySymbol,
  findSelectedVariants,
  formatMoney,
  getSelectedVariantsCompareAtPrice,
  getSelectedVariantsPrice,
  intelligemsCompareAtPrice,
  intelligemsPrice,
} from "@tablescape/utilities";
import { component } from "picoapp";

const currency = currencySymbol();

export const price = component<Context>((node, context) => {
  const priceTextNode = node.querySelector("[data-tbsp-component='text']");
  const parentBundlePriceString = node.dataset.parentBundlePrice;
  const productIdData = node.dataset.productId;
  const discountType = node.dataset.discountType;

  const parentBundlePrice = parentBundlePriceString
    ? parseInt(parentBundlePriceString)
    : undefined;

  const getPriceFromProductFormInputs = (
    checkedInputs: NodeListOf<Element>,
    parentBundlePrice: number,
    isKitted: boolean
  ) => {
    const inputPriceSum =
      Array.from(checkedInputs).reduce((acc, input: Element) => {
        const seletedVariantPrice =
          input.parentElement?.dataset.mtrProductVariantPrice;
        if (seletedVariantPrice) {
          return acc + Number(seletedVariantPrice);
        }
        return acc;
      }, 0) * 100;

    if (isKitted) {
      return inputPriceSum;
    }

    return Math.min(inputPriceSum, parentBundlePrice);
  };

  context.on(
    TablescapeEvent.VARIANT_PRICE_UPDATED,
    ({ discounts, products, selectedVariant }) => {
      if (!products) {
        return;
      }

      if (
        discountType !== "bundle" &&
        productIdData !== selectedVariant?.productId
      ) {
        return;
      }

      let selectedVariants = [];

      if (discountType === "bundle") {
        selectedVariants = findSelectedVariants(products);
      } else if (selectedVariant) {
        selectedVariants = [selectedVariant];
      } else {
        return;
      }

      let price = getSelectedVariantsPrice(selectedVariants, parentBundlePrice);

      /* The conditional statement below handles the case where not all variants of a product
      fixed bundle have been selected via UI yet, and thus is not in TablescapeContext state.
      In this case, the price is computed from data attributes of the selected swatches. */

      if (
        discountType === "bundle" &&
        Object.values(products).filter((product) => !!product).length < 2 &&
        parentBundlePrice
      ) {
        const checkedInputs = document.querySelectorAll(
          "#product-form input[type='radio'][checked]"
        );
        const isKitted =
          checkedInputs.length === 1 &&
          !!checkedInputs[0].closest("[data-kitted-product=true");

        price = getPriceFromProductFormInputs(
          checkedInputs,
          parentBundlePrice,
          isKitted
        );
      }

      let compareAtPrice = getSelectedVariantsCompareAtPrice(selectedVariants);
      const discount = discounts?.find(
        (discount) => discount.productId === productIdData
      );

      const hasDiscount =
        discount && discount.type === node.dataset.discountType;

      if (window.igData) {
        price = intelligemsPrice(selectedVariants) || price;
        compareAtPrice =
          intelligemsCompareAtPrice(selectedVariants) || compareAtPrice;
      }

      if (hasDiscount && (!compareAtPrice || price >= compareAtPrice)) {
        price -= discount.value;
      }

      if (priceTextNode) {
        priceTextNode.textContent = price
          ? `${currency}${formatMoney(
              price,
              "{{ amount_no_trailing_zeroes }}",
              false
            )}`
          : "";
      }

      if (compareAtPrice || hasDiscount || discountType === "bundle") {
        priceTextNode?.classList.add("text-special");
      } else {
        priceTextNode?.classList.remove("text-special");
      }
    }
  );
});

export const compareAtPrice = component<Context>((node, context) => {
  const priceTextNode = node.querySelector("[data-tbsp-component='text']");
  const productIdData = node.dataset.productId;
  const discountType = node.dataset.discountType;

  context.on(
    TablescapeEvent.VARIANT_PRICE_UPDATED,
    ({ discounts, products, selectedVariant }) => {
      if (!products) return;

      if (
        discountType !== "bundle" &&
        productIdData !== selectedVariant?.productId
      ) {
        return;
      }

      let selectedVariants = [];

      if (discountType === "bundle") {
        if (Object.keys(products).length < 2) {
          return;
        }
        selectedVariants = findSelectedVariants(products);
      } else if (selectedVariant) {
        selectedVariants = [selectedVariant];
      } else {
        return;
      }

      const discount = discounts?.find(
        (discount) => discount.productId === productIdData
      );

      let price = getSelectedVariantsPrice(selectedVariants);
      let _compareAtPrice = getSelectedVariantsCompareAtPrice(
        selectedVariants,
        discountType
      );

      if (window.igData) {
        price = intelligemsPrice(selectedVariants) || price;
        _compareAtPrice =
          intelligemsCompareAtPrice(selectedVariants) || _compareAtPrice;
      }

      let compareAtPrice = _compareAtPrice;
      if (
        discount &&
        discount.type === node.dataset.discountType &&
        !_compareAtPrice
      ) {
        if (!_compareAtPrice) {
          compareAtPrice = price;
        }
      }
      if (priceTextNode) {
        priceTextNode.textContent = compareAtPrice
          ? `${currency}${formatMoney(
              compareAtPrice,
              "{{ amount_no_trailing_zeroes }}",
              false
            )}`
          : "";
      }
    }
  );
});
